<template>
  <!--  Khi o che do minimized, chieu dong la 49 px (Vua du hien thi icon)-->
  <!--  <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" :minimized="isSidebarMinimized" :minimized-width="minimizedWidth">-->
  <VaSidebar
    v-model="writableVisible"
    v-model:minimized="minimized"
    :width="sidebarWidth"
    :color="color"
    :minimized-width="minimizedWidth"
  >
    <!-- Nút toggle sidebar + Logo -->
    <div class="sidebar-toggle-btn">
      <RouterLink v-if="!isSidebarMinimized" to="/" class="sidebar-logo-link">
        <img src="/tt-logo.jpg" width="26" alt="TT" />
        <span class="sidebar-logo-text">ADMIN</span>
      </RouterLink>
      <VaIconMenuCollapsed
        class="cursor-pointer"
        :class="{ 'x-flip': !isSidebarMinimized }"
        :color="collapseIconColor"
        @click="toggleSidebar"
      />
    </div>

    <div>
      <template v-for="(route, index) in filteredRoutes" :key="index">
        <!-- Nhãn section (không clickable) -->
        <div v-if="route.meta.isSection" class="sidebar-section-label">
          <span v-if="!isSidebarMinimized">{{ t(route.displayName) }}</span>
          <hr v-else class="sidebar-section-divider" />
        </div>

        <!-- Route có children (accordion thủ công) -->
        <template v-else-if="route.children && route.children.length > 0">
          <VaSidebarItem
            :active="routeHasActiveChild(route)"
            :active-color="activeColor"
            :text-color="textColor(route)"
            :aria-label="`Open category ${t(route.displayName)}`"
            role="button"
            hover-opacity="0.10"
            @click="toggleExpand(route.name)"
          >
            <VaSidebarItemContent class="py-3 pr-2 pl-4">
              <VaIcon
                v-if="route.meta.icon"
                aria-hidden="true"
                :name="route.meta.icon"
                size="20px"
                :color="iconColor(route)"
              />
              <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold">
                {{ t(route.displayName) }}
                <VaIcon :name="isExpanded(route.name) ? 'va-arrow-up' : 'va-arrow-down'" size="20px" />
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
          <div v-show="isExpanded(route.name)">
            <div v-for="(childRoute, index2) in route.children" :key="index2">
              <VaSidebarItem
                :to="{ name: childRoute.name }"
                :active="isActiveChildRoute(childRoute)"
                :active-color="activeColor"
                :text-color="textColor(childRoute)"
                :aria-label="`Visit ${t(childRoute.displayName)}`"
                hover-opacity="0.10"
              >
                <VaSidebarItemContent class="py-3 pr-2 pl-11">
                  <VaSidebarItemTitle class="leading-5 font-semibold">
                    {{ t(childRoute.displayName) }}
                  </VaSidebarItemTitle>
                </VaSidebarItemContent>
              </VaSidebarItem>
            </div>
          </div>
        </template>

        <!-- Route link thẳng -->
        <template v-else>
          <VaSidebarItem
            :to="{ name: route.name }"
            :active="routeHasActiveChild(route)"
            :active-color="activeColor"
            :text-color="textColor(route)"
            :aria-label="`Visit ${t(route.displayName)}`"
            hover-opacity="0.10"
          >
            <VaSidebarItemContent class="py-3 pr-2 pl-4">
              <VaIcon
                v-if="route.meta.icon"
                aria-hidden="true"
                :name="route.meta.icon"
                size="20px"
                :color="iconColor(route)"
              />
              <VaSidebarItemTitle class="leading-5 font-semibold flex justify-between items-center gap-2">
                <span>{{ t(route.displayName) }}</span>
                <span v-if="route.name === acrMenuName && acrBadgeText" class="sidebar-menu-badge">
                  {{ acrBadgeText }}
                </span>
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>
      </template>
    </div>

    <!-- Avatar + tên ghim dưới cùng sidebar -->
    <div class="sidebar-bottom-profile">
      <ProfileDropdown />
      <span v-if="!isSidebarMinimized" class="sidebar-fullname">{{ fullName }}</span>
    </div>
  </VaSidebar>
</template>
<script lang="ts">
import { defineComponent, watch, ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'

import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import { storeToRefs } from 'pinia'

import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'
import { useGlobalStore } from '../../stores/global-store'
import { usePermissionStore } from '@/stores/permission-store'
import ProfileDropdown from '../navbar/components/dropdowns/ProfileDropdown.vue'
import VaIconMenuCollapsed from '../icons/VaIconMenuCollapsed.vue'
import RoleCode from '@/constants/RoleCode'
import { useAdditionalCostBadgeStore } from '@/stores/additional-cost-badge-store'

export default defineComponent({
  name: 'Sidebar',
  components: { ProfileDropdown, VaIconMenuCollapsed },
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const { t } = useI18n()

    const store = useGlobalStore()
    const { isSidebarMinimized, fullName } = storeToRefs(store)

    const expandState = ref<Record<string, boolean>>({})

    const isExpanded = (name: string) => expandState.value[name] ?? false

    const toggleExpand = (name: string) => {
      expandState.value[name] = !isExpanded(name)
    }

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const minimized = computed({
      get: () => isSidebarMinimized.value,
      set: (val: boolean) => store.setSidebarMinimized(val),
    })

    const minimizedWidth = computed(() => (props.mobile ? '0px' : '49px'))

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

    // Active khi dang o dung route, HOAC o route con cung nhanh path (theo ranh gioi segment "/").
    // Dung segment thay vi endsWith de tranh trung chuoi con:
    //  - /vehicle-trackings/2/edit  -> sang menu "Theo doi xe" (vehicle-trackings)
    //  - /completed-vehicle-trackings -> KHONG sang menu "vehicle-trackings"
    const routeHasActiveChild = (section: INavigationRoute) => {
      const base = `/${section.name}`
      if (route.path === base || route.path.startsWith(`${base}/`)) return true

      if (!section.children) {
        return route.name === section.name
      }

      return section.children.some(({ name }) => route.name === name)
    }

    // Tự động mở accordion chứa route hiện tại khi điều hướng
    const setActiveExpand = () => {
      navigationRoutes.routes.forEach((topRoute) => {
        if (topRoute.meta.isSection || !topRoute.children) return
        if (routeHasActiveChild(topRoute)) {
          expandState.value[topRoute.name] = true
        }
      })
    }

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '200px'))
    const color = computed(() => getColor('background-secondary'))
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))
    const collapseIconColor = computed(() => getColor('secondary'))
    const toggleSidebar = () => store.toggleSidebar()

    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'secondary')
    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'textPrimary')

    watch(() => route.fullPath, setActiveExpand, { immediate: true })

    const roleCode = computed(() => store.roleCode)
    const level = computed(() => store.level)
    const username = computed(() => store.username)
    const permStore = usePermissionStore()

    // Quyết định 1 mục menu có hiển thị không:
    //  - Ưu tiên quyền THẬT từ BE theo can_read (resource = tên route).
    //  - Nếu /me chưa load hoặc mục không nằm trong ma trận (section, accordion cha...) -> fallback roles/level.
    const isMenuVisible = (item: INavigationRoute): boolean => {
      // Giới hạn theo username cụ thể (VD: super_admin)
      if (item.meta?.usernames && item.meta.usernames.length > 0) {
        if (!username.value || !item.meta.usernames.includes(username.value)) return false
      }

      const real = permStore.canRead(item.name) // undefined nếu không có dữ liệu
      if (real !== undefined) return real

      const currentRole = roleCode.value
      const currentLevel = level.value
      const isBOD = currentRole === RoleCode.BOD
      if (!item.meta?.roles || item.meta.roles.length === 0) return true
      if (currentRole == null || !item.meta.roles.includes(currentRole)) return false
      if (isBOD) return true
      if (item.meta.level && currentLevel < item.meta.level) return false
      return true
    }

    // Giới hạn theo username cụ thể (VD: super_admin) — áp dụng độc lập, kể cả cho nhóm cha (accordion)
    const isAllowedByUsername = (item: INavigationRoute): boolean => {
      if (item.meta?.usernames && item.meta.usernames.length > 0) {
        return !!username.value && item.meta.usernames.includes(username.value)
      }
      return true
    }

    // Con nào KHÔNG tự khai báo roles/level (vốn trước giờ chỉ được chặn gián tiếp qua role của
    // nhóm cha) thì kế thừa roles/level từ cha — tránh việc bỏ chặn ở cha làm lộ hết các con này.
    // Con nào có quyền thật riêng trong ma trận (permStore.canRead) vẫn được ưu tiên như cũ.
    const withInheritedMeta = (child: INavigationRoute, parent: INavigationRoute): INavigationRoute => ({
      ...child,
      meta: {
        ...child.meta,
        roles: child.meta.roles ?? parent.meta.roles,
        level: child.meta.level ?? parent.meta.level,
      },
    })

    // Filter de hien thi nhung MENU duoc phep show len theo quyen that (fallback role/level)
    const filteredRoutes = computed(() => {
      // Bước 1: lọc children TRƯỚC theo quyền thật/role (kế thừa roles/level từ cha nếu con chưa
      // tự khai báo). Nhãn section và nhóm cha (accordion) đều không phải resource thật trong ma
      // trận nên KHÔNG tự chặn theo meta.roles tĩnh của chính nó nữa — chỉ ẩn khi không còn mục nào
      // phía sau/bên trong nó hiển thị (vẫn tôn trọng usernames nếu có giới hạn riêng).
      const visible = navigationRoutes.routes
        .map((route) => {
          if (!route.children) return route
          const children = route.children
            .map((child) => withInheritedMeta(child, route))
            .filter((child) => isMenuVisible(child))
          return { ...route, children }
        })
        .filter((route) => {
          if (!isAllowedByUsername(route)) return false
          if (route.meta.isSection) return true // Bước 2 bên dưới sẽ tự loại nếu rỗng
          if (route.children) return route.children.length > 0
          return isMenuVisible(route)
        })

      // Bước 2: bỏ nhãn section nếu không còn mục nào thuộc section đó hiển thị.
      // Section là nhãn phẳng: nó "sở hữu" các mục đứng sau cho đến section kế tiếp.
      return visible.filter((route, index) => {
        if (!route.meta.isSection) return true
        const next = visible[index + 1]
        return next != null && !next.meta.isSection
      })
    })

    // ── Badge "Đề xuất Phát sinh": số đếm theo vai trò - xem docs/receive-backend/0017-badge-de-xuat-phat-sinh.md ──
    const ACR_MENU_NAME = 'additional-cost-requests'
    const acrBadgeStore = useAdditionalCostBadgeStore()
    const { displayText: acrBadgeText } = storeToRefs(acrBadgeStore)

    // Chỉ đếm khi mục menu này hiển thị với user (tránh gọi API khi không có quyền)
    const acrMenuVisible = computed(() =>
      filteredRoutes.value.some((r) => r.name === ACR_MENU_NAME || r.children?.some((c) => c.name === ACR_MENU_NAME)),
    )

    const refreshAcrCount = () => {
      if (acrMenuVisible.value) acrBadgeStore.fetchCounts()
    }

    // Khuyến nghị doc 0017: polling mỗi 30-60s (không có push/WebSocket)
    const ACR_POLL_INTERVAL_MS = 60000
    let acrPollTimer: ReturnType<typeof setInterval> | null = null
    onMounted(() => {
      refreshAcrCount()
      acrPollTimer = setInterval(refreshAcrCount, ACR_POLL_INTERVAL_MS)
    })
    onUnmounted(() => {
      if (acrPollTimer) clearInterval(acrPollTimer)
      acrPollTimer = null
    })

    return {
      writableVisible,
      sidebarWidth,
      acrMenuName: ACR_MENU_NAME,
      acrBadgeText,
      isSidebarMinimized,
      fullName,
      minimizedWidth,
      minimized,
      expandState,
      isExpanded,
      toggleExpand,
      color,
      activeColor,
      collapseIconColor,
      toggleSidebar,
      navigationRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      t,
      iconColor,
      textColor,
      filteredRoutes,
    }
  },
})
</script>

<style lang="scss" scoped>
.sidebar-toggle-btn {
  padding: 8px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sidebar-bottom-profile {
  position: sticky;
  bottom: 0;
  background-color: var(--va-background-secondary);
  padding: 4px 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 8px;
}

.sidebar-fullname {
  font-size: 12px;
  font-weight: 600;
  color: var(--va-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.sidebar-logo-link {
  display: flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
}

.sidebar-logo-text {
  color: #13baee;
  font-size: 13px;
  font-weight: bold;
}

.sidebar-section-label {
  padding: 16px 16px 4px;
  font-size: 13px;
  font-weight: 700;
  color: #13baee;
  user-select: none;
}

.sidebar-section-divider {
  border: none;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  margin: 8px 8px;
}

/* Badge số lượng "đang phát sinh" trên mục menu Đề xuất Phát sinh */
.sidebar-menu-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background-color: #e42222;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
}
</style>
